import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Monitor, Moon, Settings, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Theme = "system" | "light" | "dark";

interface Preferences {
  theme: Theme;
  timezone: string;
  emailNotifications: {
    deploymentFailed: boolean;
    deploymentReady: boolean;
  };
}

interface ResponseShape {
  preferences: Preferences;
}

/**
 * T149 (spec 001 Phase 10) — Account preferences page.
 *
 * Server is the source of truth (PATCH /api/v1/auth/me/preferences with
 * shallow merge). Theme additionally drives the existing client-side
 * ThemeToggle (`localStorage.of-theme` + `.dark` class on <html>) so the
 * change is visible immediately on this device and persists to new
 * devices / logins through the server prefs.
 *
 * Timezone + email-notification toggles are stored only; consumers (e.g.
 * future timestamp formatters, the email notifier) will read them back
 * via GET /api/v1/auth/me/preferences.
 */
export function AccountPreferences() {
  const qc = useQueryClient();

  const prefs = useQuery({
    queryKey: ["preferences"],
    queryFn: () => api.get<ResponseShape>(`/auth/me/preferences`).then((r) => r.preferences),
  });

  const patch = useMutation({
    mutationFn: (next: Partial<Preferences>) =>
      api.patch<ResponseShape>(`/auth/me/preferences`, next).then((r) => r.preferences),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ["preferences"] });
      const prev = qc.getQueryData<Preferences>(["preferences"]);
      if (prev) qc.setQueryData<Preferences>(["preferences"], { ...prev, ...next });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["preferences"], ctx.prev);
    },
    onSuccess: (updated) => {
      qc.setQueryData(["preferences"], updated);
    },
  });

  return (
    <div className="pt-6 space-y-4">
      <div>
        <h1 className="text-lg font-semibold inline-flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" /> Preferences
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Your defaults across devices. Changes save automatically.
        </p>
      </div>

      {prefs.isLoading && (
        <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>
      )}

      {prefs.data && (
        <>
          <ThemeCard value={prefs.data.theme} onChange={(theme) => patch.mutate({ theme })} />
          <TimezoneCard
            value={prefs.data.timezone}
            onChange={(timezone) => patch.mutate({ timezone })}
          />
          <NotificationsCard
            value={prefs.data.emailNotifications}
            onChange={(emailNotifications) => patch.mutate({ emailNotifications })}
          />
        </>
      )}
    </div>
  );
}

function ThemeCard({ value, onChange }: { value: Theme; onChange: (theme: Theme) => void }) {
  const options: Array<{ theme: Theme; label: string; icon: typeof Sun }> = [
    { theme: "system", label: "System", icon: Monitor },
    { theme: "light", label: "Light", icon: Sun },
    { theme: "dark", label: "Dark", icon: Moon },
  ];

  // Mirror the client-side ThemeToggle behaviour so the choice takes
  // effect on this device immediately. The server PATCH happens in the
  // parent's mutation; this hook only handles the local DOM/storage side.
  function applyLocal(theme: Theme) {
    const dark =
      theme === "dark" ||
      (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", dark);
    if (theme === "system") localStorage.removeItem("of-theme");
    else localStorage.setItem("of-theme", theme);
  }

  return (
    <Card title="Theme" description="How open-frontend looks on this account.">
      <div className="grid grid-cols-3 gap-2">
        {options.map(({ theme, label, icon: Icon }) => (
          <button
            key={theme}
            onClick={() => {
              applyLocal(theme);
              onChange(theme);
            }}
            className={cn(
              "flex flex-col items-center justify-center gap-1.5 rounded-md border p-3 text-xs transition-colors",
              value === theme
                ? "border-accent bg-accent-subtle/50 text-accent"
                : "border-border-muted bg-background hover:border-border",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>
    </Card>
  );
}

function TimezoneCard({
  value,
  onChange,
}: {
  value: string;
  onChange: (tz: string) => void;
}) {
  const zones = useMemo(() => {
    type WithTZ = { supportedValuesOf?: (k: string) => string[] };
    const fn = (Intl as unknown as WithTZ).supportedValuesOf;
    if (typeof fn === "function") return fn("timeZone");
    return [
      "UTC",
      "America/Los_Angeles",
      "America/New_York",
      "Europe/London",
      "Europe/Berlin",
      "Asia/Kolkata",
      "Asia/Singapore",
      "Asia/Tokyo",
      "Australia/Sydney",
    ];
  }, []);

  // Browser-guessed timezone — surfaced as a quick "use my current" link.
  const guessed = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  return (
    <Card
      title="Timezone"
      description={`Used to format timestamps. Detected: ${guessed}.`}
    >
      <div className="flex items-center gap-2">
        <select
          aria-label="Timezone"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 h-9 px-2 rounded-md border border-border bg-background text-sm font-mono"
        >
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
        {guessed !== value && (
          <Button variant="ghost" size="sm" className="shrink-0" onClick={() => onChange(guessed)}>
            Use detected
          </Button>
        )}
      </div>
    </Card>
  );
}

function NotificationsCard({
  value,
  onChange,
}: {
  value: { deploymentFailed: boolean; deploymentReady: boolean };
  onChange: (v: { deploymentFailed: boolean; deploymentReady: boolean }) => void;
}) {
  return (
    <Card
      title="Email notifications"
      description="Sent to your account email. (Wiring is forward-compatible; outbound email is a separate setting.)"
    >
      <div className="space-y-2">
        <Toggle
          label="When a deployment fails"
          checked={value.deploymentFailed}
          onChange={(deploymentFailed) => onChange({ ...value, deploymentFailed })}
        />
        <Toggle
          label="When a deployment goes live"
          checked={value.deploymentReady}
          onChange={(deploymentReady) => onChange({ ...value, deploymentReady })}
        />
      </div>
    </Card>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer text-sm">
      <span>{label}</span>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
          checked ? "bg-accent" : "bg-border",
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-background shadow transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </label>
  );
}

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="of-card">
      <div className="of-card-header">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <div className="text-2xs text-subtle-foreground">{description}</div>
        </div>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

